import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
    deleteTalentFail,
    deleteTalentSuccess,
    getSavedTalentsFail,
    getSavedTalentsSuccess,
    saveTalentFail,
    saveTalentSuccess,
    searchTalentFail,
    searchTalentSuccess,
    updateTalentFail,
    updateTalentSuccess,
    findTalentSuccess,
    findTalentFailure,
    deleteSaveTalentSuccess,
    deleteSaveTalentFail,
} from './actions';
import { searchUserTalentUrl, talentUrl, findTalentUrl } from '../../configurations/api/url';
import { api } from '../../configurations/api';
import { destroyOneCache, setCache } from '../cache/action';
import { isUseCacheEnabled, getCacheDuration, getCacheByType } from '../cache/saga';
import { CacheValue, CACHE_TYPE } from '../cache/types';
import { globalErrorHandler } from '../error/saga';
import { httpRequest } from '../types';
import { IGetSavedTalents, ISaveTalent, ITalentData, types, ITalentFilters, IDeleteSaveTalent } from './types';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';
import { IGenericDocumentModifier, IGetAllResponse } from '../../types/global/helper';
import { ISearchPayload } from '../contest/types';
import { Action } from 'redux';
import { setInAppNotification } from '../in-app-notification/actions';

function* getTalentSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IGetSavedTalents;
    resolve: (data: IGetAllResponse<ITalentData[]>) => void;
    reject: (error: any) => void;
}): any {
    const params = new URLSearchParams();
    if (payload.page) params.append('page', String(payload.page));
    if (payload.limit) params.append('limit', String(payload.limit));
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    let initialResult: any = null;
    const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.TALENT);
    if (cache && cache.key) {
        initialResult = cache.value;
    }

    if (
        !!initialResult &&
        defaultUseCache &&
        ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration
    ) {
        yield put(getSavedTalentsSuccess(initialResult));
        if (resolve) {
            resolve(initialResult);
        }
    } else {
        try {
            const request = `${talentUrl}?${params}`;

            const response = yield call(api, request, httpRequest.GET, null, 0, 0);
            const { data } = response.data;
            yield put(getSavedTalentsSuccess(data));
            yield put(
                setCache({
                    key: Date.now(),
                    value: data,
                    type: CACHE_TYPE.TALENT,
                    isUnique: true,
                }),
            );
            if (resolve) {
                resolve(response);
            }
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getSavedTalentsFail(error));
            yield call(globalErrorHandler, error);
            if (reject) {
                reject(error);
            }
        }
    }
}
function* saveTalentSaga({ payload }: { payload: ISaveTalent }): any {
    try {
        const response = yield call(api, talentUrl, httpRequest.POST, payload, 1, 2000);

        const { data } = response.data;
        yield put(saveTalentSuccess(data));
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.TALENT,
            }),
        );
        yield put(
            setInAppNotification({
                message: 'Talent Saved Successfully',
                title: 'Talent Saved Successfully',
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(saveTalentFail({ ...error, additionalPayload: payload }));
        yield call(globalErrorHandler, error);
    }
}

function* deleteSavedTalentSaga({ payload }: { payload: IDeleteSaveTalent }): any {
    try {
        yield call(api, `${talentUrl}/${payload.saveId}`, httpRequest.DELETE, {}, 1, 2000);

        yield put(deleteSaveTalentSuccess(payload));
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.TALENT,
            }),
        );
        yield put(
            setInAppNotification({
                message: 'Talent Deleted Successfully',
                title: 'Talent Deleted Successfully',
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteSaveTalentFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateTalentSaga({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        const response = yield call(
            api,
            `${talentUrl}/${payload.documentId}`,
            httpRequest.PATCH,
            payload.payload,
            1,
            2000,
        );
        const { data } = response.data;
        yield put(updateTalentSuccess(data));
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.TALENT,
            }),
        );
        yield put(
            setInAppNotification({
                message: 'Talent Updated Successfully',
                title: 'Talent Updated Successfully',
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateTalentFail(error));
        yield call(showToastMessage, 'Update Error!', 'error');
    }
}

function* searchTalentSaga({ payload }: { payload: ISearchPayload }): any {
    try {
        const response = yield call(api, searchUserTalentUrl, httpRequest.POST, payload, 2);
        const { data }: { data: ITalentData[] } = response;
        yield put(searchTalentSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(searchTalentFail(error));
    }
}

function* deleteTalentSaga({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        yield call(api, `${talentUrl}/${payload.documentId}`, httpRequest.DELETE, {}, 0, undefined, false);
        yield put(deleteTalentSuccess(payload));
        yield put(
            setInAppNotification({
                message: 'Talent Removed Successfully',
                title: 'Talent Removed Successfully',
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteTalentFail(error));
    }
}

function buildQueryParams(filters: ITalentFilters) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
                value.forEach(v => params.append(key, v));
            } else {
                params.append(key, String(value));
            }
        }
    });
    return params.toString();
}

function* findTalent({
    payload,
    resolve,
    reject,
}: {
    payload: ITalentFilters;
    resolve: (value: any) => Promise<void>;
    reject: (reason: any) => Promise<void>;
}): Generator<any, void, any> {
    try {
        let query;
        if (payload) {
            query = buildQueryParams(payload);
        }
        const url = query ? `${findTalentUrl}?${query}` : findTalentUrl;
        const response = yield call(api, url, httpRequest.GET, null, 0, 0);
        yield put(findTalentSuccess(response));
        if (resolve) resolve(response);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(findTalentFailure(error?.message || 'Failed to fetch talents'));
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

interface TaskAction extends Action {
    payload: ISaveTalent;
}
interface UpdateTalentTaskAction extends Action {
    payload: IGenericDocumentModifier;
}
interface SearchTalentTaskAction extends Action {
    payload: ISearchPayload;
}
interface DeleteTalentTaskAction extends Action {
    payload: IGenericDocumentModifier;
}

function* getTalentWatcher() {
    yield takeLatest<any>(types.GET_SAVED_TALENTS, getTalentSaga);
}

function* saveTalentWatcher() {
    yield takeLatest<TaskAction>(types.SAVE_TALENT, saveTalentSaga);
}

function* updateTalentWatcher() {
    yield takeLatest<UpdateTalentTaskAction>(types.UPDATE_TALENT, updateTalentSaga);
}

function* searchTalentWatcher() {
    yield takeLatest<SearchTalentTaskAction>(types.SEARCH_TALENT, searchTalentSaga);
}

function* deleteTalentWatcher() {
    yield takeLatest<DeleteTalentTaskAction>(types.DELETE_TALENT, deleteTalentSaga);
}

function* fetchTalentsWatcher() {
    yield takeLatest<any>(types.FIND_TALENT, findTalent);
}

function* deleteSavedTalentWatcher() {
    yield takeLatest<any>(types.DELETE_SAVED_TALENT, deleteSavedTalentSaga);
}

export default function* talentSaga() {
    yield all([
        getTalentWatcher(),
        saveTalentWatcher(),
        updateTalentWatcher(),
        searchTalentWatcher(),
        deleteTalentWatcher(),
        fetchTalentsWatcher(),
        deleteSavedTalentWatcher(),
    ]);
}
